import { Share2 } from "lucide-react";
import { cn } from "@city-sage/aurum-avis-labs-components";
import { SocialShareDialog } from "./SocialShareDialog";

interface ShareButtonProps {
  url: string;
  title: string;
  description?: string;
  hashtags?: string[];
  theme?: 'light' | 'dark';
  layout?: 'vertical' | 'horizontal';
  shareMethod?: 'dialog' | 'native';
  onClick?: () => void;
  className?: string;
}

export const ShareButton = ({
  url,
  title,
  description,
  hashtags = ['CitySage', 'Travel'],
  theme = 'light',
  layout = 'vertical',
  shareMethod = 'dialog',
  onClick,
  className
}: ShareButtonProps) => {
  const isDark = theme === 'dark';
  const isHorizontal = layout === 'horizontal';

  const handleNativeShare = async () => {
    if (onClick) {
      onClick();
      return;
    }

    if (navigator.share) {
      try {
        await navigator.share({
          title,
          text: description || `Check out: ${title}`,
          url
        });
      } catch (error) {
        // User cancelled sharing or error occurred
        console.log('Sharing cancelled or failed:', error);
      }
    } else {
      // Fallback to clipboard
      try {
        await navigator.clipboard.writeText(url);
        // Could show a toast here
        console.log('Link copied to clipboard');
      } catch (error) {
        console.error('Failed to copy link:', error);
      }
    }
  };

  const renderButton = () => (
    <button
      onClick={shareMethod === 'native' ? handleNativeShare : onClick}
      className={cn(
        "flex items-center justify-center p-1 bg-transparent border-none cursor-pointer transition-transform active:scale-95 hover:scale-105 group",
        isHorizontal ? "flex-row" : "flex-col",
        className
      )}
      aria-label={`Share ${title}`}
    >
      <Share2 
        className={cn(
          "transition-colors duration-200",
          isHorizontal ? "w-5 h-5" : "w-7 h-7",
          isDark 
            ? "text-white drop-shadow-md group-hover:text-gray-100" 
            : "text-gray-900 group-hover:text-gray-700"
        )}
        strokeWidth={2}
      />
    </button>
  );

  if (shareMethod === 'dialog') {
    return (
      <SocialShareDialog
        url={url}
        title={title}
        description={description}
        hashtags={hashtags}
      >
        {renderButton()}
      </SocialShareDialog>
    );
  }

  return renderButton();
};
